// src/lib/analytics/registry.ts
/**
 * @fileoverview Canonical analytics event registry.
 * @description The single source of truth for every analytics event the site
 * emits: the typed property shape of each event, plus its metadata — GA4 event
 * name, Vercel mirror name, and `event_category` (all consulted at runtime by
 * the fan-out), and a `conversion` flag that is a CODE-SIDE RECORD only (GA4 Key
 * Event flagging is done manually in the GA4 UI; `isConversionEvent` is not
 * consulted in the emit path).
 *
 * See `docs/analytics/event-taxonomy.md` for the naming convention this file
 * encodes. The short version:
 * - event names are `snake_case`, `object_action`, GA4-reserved-name-safe;
 * - high-cardinality values (slugs, queries, prices) are PROPERTIES, never names;
 * - every event has a closed-set `category`;
 * - the typed `track(name, params)` API (see `./core`) enforces the property
 *   shape at compile time for migrated events.
 *
 * Two surfaces live here:
 * - `AnalyticsEventParams` — the COMPILE-TIME map (event name → typed props) for
 *   events emitted through the typed `track()` API.
 * - `EVENT_REGISTRY` — the RUNTIME metadata for EVERY known event (including the
 *   long-tail ad-hoc events still emitted via the legacy `sendEvent` shim), used
 *   to drive the GA4 → Vercel mirror, conversion flagging, category injection,
 *   and dev-time "unregistered event" validation.
 */

//=============================================================================
// Shared vocabulary (reuse these — do not coin synonyms)
//=============================================================================

/** Closed set of event groupings (GA4 `event_category` + dashboard grouping). */
export type EventCategory =
  | "page"
  | "resource"
  | "navigation"
  | "discovery"
  | "engagement"
  | "lead"
  | "request_access"
  | "commerce"
  | "privacy"
  | "error"

/** Property value types GA4 + Vercel accept. Objects/arrays must be flattened. */
export type EventPropValue = string | number | boolean

/** Content resource kinds. */
export type ResourceType =
  | "hub"
  | "category"
  | "tool"
  | "template"
  | "guide"
  | "calculator"
  | "quiz"
  | "glossary"
  | "analyzer"
  | "generator"

/** Within-resource interaction kinds (the `interaction_type` property). */
export type InteractionType =
  | "start"
  | "input_change"
  | "answer_select"
  | "answer_submit"
  | "submit"
  | "reset"
  | "retake"
  | "tab_switch"
  | "section_navigation"
  | "link_click"
  | "hint_usage"
  | "settings_change"
  | "configuration_change"
  | "image_upload"
  | "download"
  | "email_capture"
  | "share"
  | "copy_result"
  | "filter"
  | "search"
  | "search_result_click"
  | "navigation"
  | "cta_click"

/** Completion outcome for `resource_complete`. */
export type Outcome = "success" | "partial" | "abandoned"

/** SKUs the storefront sells (ads + email framework/skills). Single source of
 *  truth — `~/lib/ads-framework/storefront-analytics.ts` imports this. */
export type StorefrontProduct =
  | "ads-framework"
  | "ads-skills-only"
  | "email-framework"
  | "email-skills-only"

//=============================================================================
// Typed property shapes (compile-time map for the `track()` API)
//=============================================================================

/** Shared base for every `resource_*` event. */
export interface ResourceParams {
  resource_type: ResourceType
  resource_slug?: string
}

/** Shared step dimensions for the request-access funnel. */
export interface RequestAccessStepParams {
  step_id: string
  step_number: number
  total_steps: number
  step_title?: string
}

/**
 * First-party acquisition attribution attached to conversion events. Produced
 * by `~/lib/analytics/attribution` (`getAttributionProperties`) and merged into
 * `request_access_submit`, `purchase`, `begin_checkout`, and `view_item` so each
 * conversion answers "where did this come from" — first/last campaign touch, the
 * external referrer, the landing page, and the on-site page that referred it —
 * directly on the GA4 event AND the Vercel conversion. Every field is optional:
 * the bag is empty for an opted-out visitor (storage was never written) and
 * individual keys are omitted when not applicable (e.g. no `*_campaign` for a
 * direct visit). See `docs/analytics/event-taxonomy.md` § Attribution.
 */
export interface AttributionParams {
  /** First touch (set once, never overwritten). */
  first_touch_source: string
  first_touch_medium: string
  first_touch_campaign: string
  first_touch_term: string
  first_touch_content: string
  first_touch_referrer: string
  /** Path of the page the first touch landed on. */
  first_landing_path: string
  /** Last campaign/referral touch before the conversion. */
  last_touch_source: string
  last_touch_medium: string
  last_touch_campaign: string
  last_touch_term: string
  last_touch_content: string
  last_touch_referrer: string
  /** Count of distinct campaign/referral entries (multi-touch depth). */
  attribution_touch_count: number
  /** Whole days between the first touch and the conversion. */
  days_since_first_touch: number
  /** On-site page visited immediately before the conversion page (internal referral). */
  referrer_path: string
  /** Coarse site section of the first landing page (site-section strength). */
  entry_section: string
  /** Coarse site section of the internal referrer page. */
  referrer_section: string
}

/**
 * Compile-time map of event name → property shape, for events emitted through
 * the typed `track()` API. Adding an entry here (and to `EVENT_REGISTRY`) is how
 * you register a new typed event.
 */
export interface AnalyticsEventParams {
  // --- page -------------------------------------------------------------
  page_view: {
    page_path: string
    page_location?: string
    page_title?: string
    viewport_bucket?: string
    in_app_browser?: boolean
    in_app_app?: string
    region_consent_required?: boolean
    analytics_consent_state?: string
    storage_attribution_present?: boolean
    /** Coarse site section for the current page (site intelligence). */
    site_section?: string
    /** Funnel stage of the current page. */
    page_intent?: string
    /** 1-based page index within the browser tab session. */
    session_page_index?: number
    /** Deepest funnel stage reached this session before this page. */
    session_max_intent?: string
    /** Count of distinct site sections visited this session. */
    session_section_count?: number
    session_action_count?: number
    session_high_intent_actions?: number
    session_active_time_msec?: number
    session_engagement_tier?: string
  }

  /** Fired once when the visitor leaves a page (tab hidden / navigate away). */
  page_engagement: {
    page_path: string
    site_section: string
    page_intent: string
    session_page_index: number
    time_on_page_msec: number
    max_scroll_depth_pct: number
    synthetic_scroll_depth?: boolean
    session_action_count?: number
    session_engagement_tier?: string
  }

  /** Unified explicit UI action (CTA, nav, toggle, outbound). */
  site_action: {
    action_kind: string
    action_label: string
    action_surface: string
    high_intent_action: boolean
    element_text?: string
    destination?: string
    site_section?: string
    page_intent?: string
    session_page_index?: number
    session_action_count?: number
    session_engagement_tier?: string
  }

  /** Terminal session summary when the tab closes or goes idle. */
  session_engagement: {
    session_page_index: number
    session_action_count: number
    session_high_intent_actions: number
    session_active_time_msec: number
    session_engagement_tier: string
    session_max_intent: string
    session_section_count: number
  }

  // --- resource ---------------------------------------------------------
  resource_view: ResourceParams & Record<string, EventPropValue>
  resource_category_view: ResourceParams & Record<string, EventPropValue>
  resource_interact: ResourceParams & {
    interaction_type: InteractionType
  } & Record<string, EventPropValue>
  resource_complete: ResourceParams & {
    outcome?: Outcome
  } & Record<string, EventPropValue>
  resource_tab_switch: ResourceParams & {
    from_tab: string
    to_tab: string
    interaction_type: "tab_switch"
  }
  resource_link_click: {
    link_type: "external" | "internal"
    link_url: string
    link_domain: string
    link_title?: string
    source_resource?: string
    source_resource_type?: string
    link_category?: string
    interaction_type: "link_click"
  }
  resource_section_navigation: {
    resource_type: string
    resource_slug: string
    from_section: string
    to_section: string
    navigation_method: string
    interaction_type: "section_navigation"
  }
  resource_download: {
    file_name: string
    file_type: string
    download_url: string
    source_resource?: string
    source_resource_type?: string
    interaction_type: "download"
  }

  // --- discovery --------------------------------------------------------
  search: {
    search_term: string
    search_type: string
    result_count: number
    source_location?: string
    interaction_type: "search"
  }
  filter_content: {
    content_type: string
    filter_type: string
    filter_value: string
    result_count: number
    active_filter_count: number
    active_filters?: string
    interaction_type: "filter"
  }
  search_result_click: {
    search_term: string
    result_type: string
    result_position: number
    result_title: string
    interaction_type: "search_result_click"
  }

  // --- navigation -------------------------------------------------------
  site_navigation: {
    from_section: string
    to_section: string
    navigation_method: string
    from_resource?: string
    interaction_type: "navigation"
  }

  // --- lead -------------------------------------------------------------
  email_capture: {
    capture_context: string
    source_page?: string
    source_element?: string
    is_return_user?: boolean
    interaction_type: "email_capture"
  }

  // --- engagement -------------------------------------------------------
  scroll_depth: {
    scroll_depth_pct: number
  }
  button_click: {
    placement?: string
    destination_path?: string
    surface_path?: string
    interaction_type?: InteractionType
    plan_tier?: string
    link_text?: string
    bridge_target?: string
    element_id?: string
  } & Record<string, EventPropValue>

  experiment_exposure: {
    experiment_id: string
    variant_id: string
    experiment_kind?: string
    surface_path?: string
    surface_scope?: string
  }

  // --- request-access funnel -------------------------------------------
  request_access_start: Record<string, never>
  request_access_step_view: RequestAccessStepParams
  request_access_step_complete: RequestAccessStepParams
  request_access_submit: {
    plan_tier?: string
    company_size?: string
    monthly_ad_spend?: string
    platforms?: string
    platform_count?: number
  } & Partial<AttributionParams>
  request_access_error: {
    error_type: string
  }

  // --- commerce ---------------------------------------------------------
  begin_checkout: {
    product: StorefrontProduct
    currency: string
    placement?: string
    surface_path?: string
    value?: number
  } & Partial<AttributionParams>
  checkout_error: {
    product: StorefrontProduct
    status?: number
    placement?: string
    surface_path?: string
  }
  purchase: {
    transaction_id: string
    value: number
    currency: string
    product: StorefrontProduct
  } & Partial<AttributionParams>
  framework_download: {
    download_action: "gate_open" | "gate_cancel" | "first_download_intent" | "re_download"
    product: StorefrontProduct
    license_id: string
  }
  view_item: {
    product: StorefrontProduct
    value: number
    currency: string
    placement?: string
    surface_path?: string
  } & Partial<AttributionParams>

  // --- reliability ------------------------------------------------------
  exception: {
    description: string
    fatal: boolean
    component?: string
    error_id?: string
  }

  // --- 404 --------------------------------------------------------------
  page_not_found: {
    page_path: string
    referrer?: string
  }

  // --- privacy ----------------------------------------------------------
  consent_update: {
    analytics_consent: boolean
    functional_consent: boolean
    ads_consent: boolean
    do_not_sell_share: boolean
    gpc_detected: boolean
  }
  consent_banner_shown: {
    surface: string
  }
  consent_banner_interaction: {
    action: "accept_all" | "reject_non_essential" | "manage"
  }
}

/** Every event name with a compile-time-typed property shape. */
export type AnalyticsEventName = keyof AnalyticsEventParams

//=============================================================================
// Runtime metadata for every known event
//=============================================================================

/**
 * Runtime metadata for one event.
 * - `vercel`: the Vercel custom-event name to mirror to, or `null` for
 *   GA4-only. `null` is also correct for events whose Vercel half is dispatched
 *   explicitly by a dedicated module (so the generic mirror can't double-fire).
 * - `category`: the GA4 `event_category` grouping (injected if a call site omits it).
 * - `conversion`: GA4 Key Event / Vercel conversion (revenue or lead signal).
 */
export interface EventSpec {
  readonly vercel: string | null
  readonly category: EventCategory
  readonly conversion: boolean
}

/**
 * The complete registry. Keys are GA4 event names. The `vercel` field preserves
 * exactly the historical GA4 → Vercel mirror (so first-party Vercel series stay
 * continuous): only the resource/discovery/navigation/email family mirrors via
 * the generic path; commerce + funnel events carry `vercel: null` because their
 * dedicated modules dispatch the Vercel half (often as the site-wide
 * `conversion` event) with bespoke readiness handling.
 */
export const EVENT_REGISTRY: Readonly<Record<string, EventSpec>> = {
  // page
  page_view: { vercel: null, category: "page", conversion: false },
  page_engagement: { vercel: null, category: "engagement", conversion: false },
  site_action: { vercel: "site_action", category: "engagement", conversion: false },
  session_engagement: { vercel: null, category: "engagement", conversion: false },

  // resource (generic mirror)
  resource_view: { vercel: "resource_view", category: "resource", conversion: false },
  resource_category_view: { vercel: "resource_category_view", category: "resource", conversion: false },
  resource_interact: { vercel: "resource_interact", category: "resource", conversion: false },
  resource_complete: { vercel: "resource_complete", category: "resource", conversion: false },
  resource_tab_switch: { vercel: "navigation", category: "navigation", conversion: false },
  resource_link_click: { vercel: "navigation", category: "navigation", conversion: false },
  resource_section_navigation: { vercel: "navigation", category: "navigation", conversion: false },
  resource_download: { vercel: "download", category: "resource", conversion: false },

  // discovery (generic mirror)
  search: { vercel: "search", category: "discovery", conversion: false },
  filter_content: { vercel: "filter", category: "discovery", conversion: false },
  search_result_click: { vercel: "navigation", category: "discovery", conversion: false },

  // navigation (generic mirror)
  site_navigation: { vercel: "navigation", category: "navigation", conversion: false },

  // lead (generic mirror)
  email_capture: { vercel: "email_capture", category: "lead", conversion: false },
  signup_attempt: { vercel: null, category: "lead", conversion: false },
  signup_success: { vercel: null, category: "lead", conversion: false },
  signup_error: { vercel: null, category: "lead", conversion: false },
  sign_up_click: { vercel: null, category: "lead", conversion: false },
  form_submission: { vercel: null, category: "lead", conversion: false },

  // engagement
  scroll_depth: { vercel: null, category: "engagement", conversion: false },
  experiment_exposure: { vercel: "experiment_exposure", category: "engagement", conversion: false },
  button_click: { vercel: null, category: "engagement", conversion: false },
  cta_button_click: { vercel: null, category: "engagement", conversion: false },
  footer_link_click: { vercel: null, category: "navigation", conversion: false },
  // ResourceDoormat band clicks (delegated island, mirrors footer_link_click;
  // Vercel mirror rides on site_action like the footer's).
  doormat_link_click: { vercel: null, category: "navigation", conversion: false },
  hero_video_clicked: { vercel: null, category: "engagement", conversion: false },
  theme_change: { vercel: null, category: "engagement", conversion: false },
  billing_frequency_change: { vercel: null, category: "engagement", conversion: false },
  faq_interact: { vercel: null, category: "engagement", conversion: false },
  reload_attempt: { vercel: null, category: "engagement", conversion: false },
  video_play: { vercel: null, category: "engagement", conversion: false },
  video_pause: { vercel: null, category: "engagement", conversion: false },
  video_complete: { vercel: null, category: "engagement", conversion: false },
  video_progress: { vercel: null, category: "engagement", conversion: false },

  // request-access funnel (dedicated module dispatches Vercel → vercel: null)
  request_access_start: { vercel: null, category: "request_access", conversion: false },
  request_access_step_view: { vercel: null, category: "request_access", conversion: false },
  request_access_step_complete: { vercel: null, category: "request_access", conversion: false },
  request_access_submit: { vercel: null, category: "request_access", conversion: true },
  request_access_error: { vercel: null, category: "request_access", conversion: false },

  // commerce (dedicated module dispatches Vercel → vercel: null)
  begin_checkout: { vercel: null, category: "commerce", conversion: false },
  checkout_error: { vercel: null, category: "commerce", conversion: false },
  purchase: { vercel: null, category: "commerce", conversion: true },
  framework_download: { vercel: null, category: "commerce", conversion: false },
  // view_item: GA4 recommended ecommerce; dedicated module dispatches Vercel.
  view_item: { vercel: null, category: "commerce", conversion: false },

  // reliability (GA4 'exception' recommended event)
  exception: { vercel: null, category: "error", conversion: false },

  // 404
  page_not_found: { vercel: null, category: "navigation", conversion: false },

  // privacy
  consent_update: { vercel: null, category: "privacy", conversion: false },
  consent_banner_shown: { vercel: null, category: "privacy", conversion: false },
  consent_banner_interaction: { vercel: null, category: "privacy", conversion: false },
}

//=============================================================================
// Lookups
//=============================================================================

/** Whether an event name is declared in the registry. */
export function isRegisteredEvent(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(EVENT_REGISTRY, name)
}

/** The spec for an event, or `undefined` if unregistered. */
export function getEventSpec(name: string): EventSpec | undefined {
  return EVENT_REGISTRY[name]
}

/** The Vercel mirror name for a GA4 event, or `null` if it should not mirror. */
export function vercelEventFor(name: string): string | null {
  return EVENT_REGISTRY[name]?.vercel ?? null
}

/** The `event_category` for an event, or `undefined` if unregistered. */
export function categoryFor(name: string): EventCategory | undefined {
  return EVENT_REGISTRY[name]?.category
}

/** Whether an event is a GA4 Key Event / Vercel conversion. */
export function isConversionEvent(name: string): boolean {
  return EVENT_REGISTRY[name]?.conversion ?? false
}
